import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  TimeEntriesService,
  TimesheetEntryDto,
  WorkItemDto,
  WorkItemsService,
  WorkspaceUserDto,
  WorkspacesService,
} from '../../../../api';
import { AuthStore } from '../../../../auth/services/auth.store';
import { PageWrapperComponent } from '../../../../shared/pages/page-wrapper/page-wrapper.component';
import { ErrorHandlerService } from '../../../../shared/services/error-handler.service';
import { ModalService } from '../../../../shared/services/modal.service';
import { DateTimeUtils } from '../../../../shared/utils/date-time-utils';
import { Button } from 'primeng/button';
import { Card } from 'primeng/card';
import { Divider } from 'primeng/divider';
import { Select } from 'primeng/select';
import { finalize, Subject, takeUntil } from 'rxjs';
import { TimesheetDayModalComponent } from '../../components/timesheet-day-modal/timesheet-day-modal.component';
import {
  TimesheetCalendarDayCell,
  TimesheetCalendarWorkItemGroup,
} from '../../model/timesheet-calendar-day';

@Component({
  selector: 'app-timesheet-calendar-page',
  standalone: true,
  imports: [CommonModule, FormsModule, PageWrapperComponent, Button, Card, Divider, Select],
  templateUrl: './timesheet-calendar-page.component.html',
  styleUrl: './timesheet-calendar-page.component.css',
})
export class TimesheetCalendarPageComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly authStore = inject(AuthStore);
  private readonly timeEntriesService = inject(TimeEntriesService);
  private readonly workItemsService = inject(WorkItemsService);
  private readonly workspacesService = inject(WorkspacesService);
  private readonly modalService = inject(ModalService);
  private readonly errorHandler = inject(ErrorHandlerService);
  private readonly destroy$ = new Subject<void>();

  readonly hydrated = signal(false);
  readonly loadingData = signal(false);
  readonly monthDate = signal<Date>(this.startOfMonth(new Date()));
  readonly selectedDayKey = signal<string | null>(null);
  readonly timeEntries = signal<TimesheetEntryDto[]>([]);
  readonly workItems = signal<WorkItemDto[]>([]);
  readonly workspaceUsers = signal<WorkspaceUserDto[]>([]);
  readonly selectedUserId = signal<string | null>(null);

  readonly activeWorkspaceId = computed(() => this.authStore.defaultWorkspace()?.id!);
  readonly currentUserId = computed(() => this.authStore.user()?.id);
  readonly ownerMode = computed(() => this.authStore.isOwner());
  readonly selectedUser = computed(
    () => this.workspaceUsers().find((user) => user.id === this.selectedUserId()) ?? null,
  );
  readonly pageTitle = computed(() =>
    this.ownerMode() ? 'Kalendarz timesheet - widok właściciela' : 'Kalendarz timesheet',
  );
  readonly monthLabel = computed(() =>
    this.monthDate().toLocaleDateString('pl-PL', {
      month: 'long',
      year: 'numeric',
    }),
  );
  readonly monthOptions = [
    { label: 'Styczeń', value: 0 },
    { label: 'Luty', value: 1 },
    { label: 'Marzec', value: 2 },
    { label: 'Kwiecień', value: 3 },
    { label: 'Maj', value: 4 },
    { label: 'Czerwiec', value: 5 },
    { label: 'Lipiec', value: 6 },
    { label: 'Sierpień', value: 7 },
    { label: 'Wrzesień', value: 8 },
    { label: 'Październik', value: 9 },
    { label: 'Listopad', value: 10 },
    { label: 'Grudzień', value: 11 },
  ];
  readonly yearOptions = computed(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 11 }, (_, index) => currentYear - 4 + index);
  });
  readonly selectedMonth = computed(() => this.monthDate().getMonth());
  readonly selectedYear = computed(() => this.monthDate().getFullYear());
  readonly ownerUserOptions = computed(() =>
    this.workspaceUsers().map((user) => ({
      ...user,
      fullName: `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim(),
    })),
  );
  readonly entriesByDay = computed(() => this.groupEntriesByDay(this.timeEntries()));
  readonly calendarDays = computed(() => this.buildCalendarDays());
  readonly calendarWeeks = computed(() => {
    const days = this.calendarDays();
    const weeks: TimesheetCalendarDayCell[][] = [];

    for (let index = 0; index < days.length; index += 7) {
      weeks.push(days.slice(index, index + 7));
    }

    return weeks;
  });
  readonly collapsedWeekIndices = signal<Set<number>>(new Set());
  readonly collapsedGroupIds = signal<Set<string>>(new Set());
  readonly selectedDayEntries = computed(() => {
    const key = this.selectedDayKey();
    if (!key) {
      return [];
    }

    return this.entriesByDay().get(key) ?? [];
  });
  readonly selectedDayGroups = computed(() =>
    this.groupEntriesByWorkItem(this.selectedDayEntries()),
  );
  readonly selectedDayTotalDuration = computed(() =>
    this.selectedDayEntries().reduce((acc, entry) => acc + (entry.duration ?? 0), 0),
  );
  readonly monthTotalDuration = computed(() =>
    this.timeEntries().reduce((acc, entry) => acc + (entry.duration ?? 0), 0),
  );
  readonly monthEntryCount = computed(() => this.timeEntries().length);
  readonly selectedDayLabel = computed(() => {
    const key = this.selectedDayKey();
    if (!key) {
      return 'Wybierz dzień';
    }

    const date = this.parseDateKey(key);
    if (!date) {
      return 'Wybierz dzień';
    }

    return date.toLocaleDateString('pl-PL', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  });

  constructor() {
    effect(() => {
      const workspaceId = this.activeWorkspaceId();
      if (workspaceId && this.ownerMode()) {
        this.fetchWorkspaceUsers();
      }

      if (workspaceId) {
        this.fetchWorkItems();
      }
    });

    effect(() => {
      const currentUserId = this.currentUserId();

      if (!this.selectedUserId() && currentUserId) {
        this.selectedUserId.set(currentUserId);
      }
    });

    effect(() => {
      const workspaceId = this.activeWorkspaceId();
      const userId = this.selectedUserId();
      const month = this.monthDate();

      if (this.hydrated() && workspaceId && userId && month) {
        this.loadCalendarData();
      }
    });
  }

  ngOnInit(): void {
    this.route.queryParamMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const monthParam = params.get('month');
      const userIdParam = params.get('userId');
      const dayParam = params.get('day');

      this.monthDate.set(
        monthParam
          ? (this.parseMonthParam(monthParam) ?? this.startOfMonth(new Date()))
          : this.startOfMonth(new Date()),
      );

      if (this.ownerMode()) {
        this.selectedUserId.set(userIdParam ?? this.currentUserId() ?? null);
      } else {
        this.selectedUserId.set(this.currentUserId() ?? null);
      }

      this.selectedDayKey.set(
        this.parseDayKey(dayParam) ?? this.getDefaultSelectedDayKey(this.monthDate()),
      );
      this.hydrated.set(true);

      if (!monthParam || (!dayParam && !this.selectedDayKey())) {
        this.updateUrl();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected goToPreviousMonth(): void {
    const nextMonth = new Date(this.monthDate());
    nextMonth.setMonth(nextMonth.getMonth() - 1);
    this.monthDate.set(this.startOfMonth(nextMonth));
    this.selectedDayKey.set(this.getDefaultSelectedDayKey(this.monthDate()));
    this.updateUrl();
  }

  protected goToNextMonth(): void {
    const nextMonth = new Date(this.monthDate());
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    this.monthDate.set(this.startOfMonth(nextMonth));
    this.selectedDayKey.set(this.getDefaultSelectedDayKey(this.monthDate()));
    this.updateUrl();
  }

  protected goToCurrentMonth(): void {
    this.monthDate.set(this.startOfMonth(new Date()));
    this.selectedDayKey.set(this.getDefaultSelectedDayKey(this.monthDate()));
    this.updateUrl();
  }

  protected onMonthChange(monthIndex: number | null): void {
    if (monthIndex == null || monthIndex < 0 || monthIndex > 11) {
      return;
    }

    this.setMonth(this.selectedYear(), monthIndex);
  }

  protected onYearChange(year: number | null): void {
    if (year == null || !Number.isFinite(year)) {
      return;
    }

    this.setMonth(year, this.selectedMonth());
  }

  protected selectDay(cell: TimesheetCalendarDayCell): void {
    this.selectedDayKey.set(cell.dateKey);
  }

  protected onOwnerUserChange(userId: string | null): void {
    if (!userId) {
      return;
    }

    this.selectedUserId.set(userId);
    this.updateUrl();
  }

  protected openDayGroup(group: TimesheetCalendarWorkItemGroup): void {
    const workspaceId = this.activeWorkspaceId();
    const userId = this.selectedUserId();
    const selectedDayKey = this.selectedDayKey();

    if (!workspaceId || !userId || !selectedDayKey) {
      return;
    }

    this.modalService.open(TimesheetDayModalComponent, {
      workspaceId,
      userId,
      dateIso: selectedDayKey,
      workItemId: group.workItemId,
      workItemName: group.workItemName,
      clientName: group.clientName,
      workItems: this.workItems(),
      onChanged: () => this.loadCalendarData(),
    });
  }

  protected openNewEntryForDay(
    workItem?: Pick<TimesheetCalendarWorkItemGroup, 'workItemId' | 'workItemName' | 'clientName'>,
  ): void {
    const workspaceId = this.activeWorkspaceId();
    const userId = this.selectedUserId();
    const selectedDayKey = this.selectedDayKey();

    if (!workspaceId || !userId || !selectedDayKey) {
      return;
    }

    this.modalService.open(TimesheetDayModalComponent, {
      workspaceId,
      userId,
      dateIso: selectedDayKey,
      workItemId: workItem?.workItemId,
      workItemName: workItem?.workItemName,
      clientName: workItem?.clientName,
      workItems: this.workItems(),
      onChanged: () => this.loadCalendarData(),
    });
  }

  protected openEntry(entry: TimesheetEntryDto): void {
    const workspaceId = this.activeWorkspaceId();
    const userId = this.selectedUserId();
    const selectedDayKey = this.selectedDayKey();

    if (!workspaceId || !userId || !selectedDayKey || !entry.workItemId) {
      return;
    }

    this.modalService.open(TimesheetDayModalComponent, {
      workspaceId,
      userId,
      dateIso: selectedDayKey,
      workItemId: entry.workItemId,
      workItemName: entry.workItemName,
      clientName: entry.clientName,
      workItems: this.workItems(),
      selectedEntryId: entry.id,
      onChanged: () => this.loadCalendarData(),
    });
  }

  protected formatDuration(minutes?: number | null): string {
    if (!minutes) {
      return '0 min';
    }

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (hours === 0) {
      return `${remainingMinutes} min`;
    }

    if (remainingMinutes === 0) {
      return `${hours} h`;
    }

    return `${hours} h ${remainingMinutes} min`;
  }

  protected formatTimeRange(start?: string | null, end?: string | null): string {
    if (!start || !end) {
      return '--:--';
    }

    return `${this.formatTime(start)} - ${this.formatTime(end)}`;
  }

  protected formatDateTime(value?: string | null): string {
    if (!value) {
      return '--';
    }

    return new Date(value).toLocaleString('pl-PL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  protected trackByDateKey(_: number, cell: TimesheetCalendarDayCell): string {
    return cell.dateKey;
  }

  protected trackByWeek(index: number): number {
    return index;
  }

  protected isWeekCollapsed(index: number): boolean {
    return this.collapsedWeekIndices().has(index);
  }

  protected toggleWeek(index: number): void {
    this.collapsedWeekIndices.update((current) => {
      const next = new Set(current);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }

      return next;
    });
  }

  protected isGroupCollapsed(groupId: string): boolean {
    return this.collapsedGroupIds().has(groupId);
  }

  protected toggleGroup(groupId: string): void {
    this.collapsedGroupIds.update((current) => {
      const next = new Set(current);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }

      return next;
    });
  }

  protected trackByGroup(index: number, group: TimesheetCalendarWorkItemGroup): string {
    return `${group.workItemId}-${index}`;
  }

  protected trackByEntry(_: number, entry: TimesheetEntryDto): string | undefined {
    return entry.id;
  }

  private loadCalendarData(): void {
    const workspaceId = this.activeWorkspaceId();
    const userId = this.selectedUserId();
    const month = this.monthDate();

    if (!workspaceId || !userId || !month) {
      return;
    }

    const { start, end } = this.getMonthRangeUTC(month);
    this.loadingData.set(true);

    this.timeEntriesService
      .getTimesheet(workspaceId, userId, start.toISOString(), end.toISOString(), 0, 0, {
        start: start.toISOString(),
        end: end.toISOString(),
        page: 0,
        pageSize: 0,
      })
      .pipe(
        finalize(() => {
          this.loadingData.set(false);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (entries) => {
          this.timeEntries.set(entries);

          if (!this.selectedDayKey()) {
            this.selectedDayKey.set(this.getDefaultSelectedDayKey(month));
          }
        },
        error: (err) => this.errorHandler.handleError(err),
      });
  }

  private fetchWorkspaceUsers(): void {
    const activeWorkspaceId = this.activeWorkspaceId();
    if (!activeWorkspaceId) {
      return;
    }

    this.workspacesService
      .getWorkspaceUsers(activeWorkspaceId, 0, 200, ['lastName,asc', 'firstName,asc'])
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.workspaceUsers.set(response.content ?? []);
        },
        error: (err) => this.errorHandler.handleError(err),
      });
  }

  private fetchWorkItems(): void {
    const activeWorkspaceId = this.activeWorkspaceId();
    if (!activeWorkspaceId) {
      return;
    }

    this.workItemsService
      .getWorkItemsFilter(activeWorkspaceId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (workItems: WorkItemDto[]) => {
          this.workItems.set(workItems);
        },
        error: (err) => this.errorHandler.handleError(err),
      });
  }

  private updateUrl(): void {
    const queryParams: Record<string, string> = {
      month: this.formatMonthParam(this.monthDate()),
    };

    const selectedUserId = this.selectedUserId();
    if (this.ownerMode() && selectedUserId) {
      queryParams['userId'] = selectedUserId;
    }

    this.router.navigate([], {
      queryParams,
      replaceUrl: true,
    });
  }

  private buildCalendarDays(): TimesheetCalendarDayCell[] {
    const month = this.monthDate();
    const firstDayOfMonth = this.startOfMonth(month);
    const firstGridDay = new Date(firstDayOfMonth);
    const firstWeekday = firstGridDay.getDay() === 0 ? 7 : firstGridDay.getDay();
    firstGridDay.setDate(firstGridDay.getDate() - (firstWeekday - 1));

    const todayKey = this.toLocalDateKey(new Date());
    const selectedDayKey = this.selectedDayKey();
    const entriesByDay = this.entriesByDay();

    return Array.from({ length: 42 }, (_, index) => {
      const date = new Date(firstGridDay);
      date.setDate(firstGridDay.getDate() + index);

      const dateKey = this.toLocalDateKey(date);
      const entries = entriesByDay.get(dateKey) ?? [];

      return {
        date,
        dateKey,
        inCurrentMonth:
          date.getMonth() === month.getMonth() && date.getFullYear() === month.getFullYear(),
        isToday: dateKey === todayKey,
        isSelected: dateKey === selectedDayKey,
        entries,
        totalDuration: entries.reduce((acc, entry) => acc + (entry.duration ?? 0), 0),
        entryCount: entries.length,
        workItemCount: new Set(entries.map((entry) => entry.workItemId)).size,
      };
    });
  }

  private groupEntriesByDay(entries: TimesheetEntryDto[]): Map<string, TimesheetEntryDto[]> {
    const map = new Map<string, TimesheetEntryDto[]>();

    [...entries]
      .sort((left, right) => {
        const leftStart = left.start ? new Date(left.start).getTime() : 0;
        const rightStart = right.start ? new Date(right.start).getTime() : 0;
        return leftStart - rightStart;
      })
      .forEach((entry) => {
        if (!entry.start) {
          return;
        }

        const key = this.toLocalDateKey(new Date(entry.start));
        const current = map.get(key) ?? [];
        current.push(entry);
        map.set(key, current);
      });

    return map;
  }

  private groupEntriesByWorkItem(entries: TimesheetEntryDto[]): TimesheetCalendarWorkItemGroup[] {
    const groups = new Map<string, TimesheetCalendarWorkItemGroup>();

    [...entries]
      .sort((left, right) => {
        const leftStart = left.start ? new Date(left.start).getTime() : 0;
        const rightStart = right.start ? new Date(right.start).getTime() : 0;
        return leftStart - rightStart;
      })
      .forEach((entry) => {
        const workItemId = entry.workItemId ?? 'unknown';
        const group = groups.get(workItemId);

        if (group) {
          group.entries.push(entry);
          group.totalDuration += entry.duration ?? 0;
          return;
        }

        groups.set(workItemId, {
          workItemId,
          workItemName: entry.workItemName ?? null,
          clientName: entry.clientName ?? null,
          color: null,
          entries: [entry],
          totalDuration: entry.duration ?? 0,
        });
      });

    return [...groups.values()].sort((left, right) => {
      const leftStart = left.entries[0]?.start ? new Date(left.entries[0].start!).getTime() : 0;
      const rightStart = right.entries[0]?.start ? new Date(right.entries[0].start!).getTime() : 0;
      return leftStart - rightStart;
    });
  }

  private getMonthRangeUTC(date: Date): { start: Date; end: Date } {
    const startLocal = new Date(date.getFullYear(), date.getMonth(), 1, 0, 0, 0, 0);
    const endLocal = new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59, 999);

    return {
      start: DateTimeUtils.localToUTC(startLocal),
      end: DateTimeUtils.localToUTCEndDay(endLocal),
    };
  }

  private parseMonthParam(value: string): Date | null {
    const match = /^(\d{4})-(\d{2})$/.exec(value);
    if (!match) {
      return null;
    }

    const year = Number(match[1]);
    const month = Number(match[2]);
    if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) {
      return null;
    }

    return this.startOfMonth(new Date(year, month - 1, 1));
  }

  private parseDateKey(value: string | null): Date | null {
    if (!value) {
      return null;
    }

    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (!match) {
      return null;
    }

    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    if (
      !Number.isFinite(year) ||
      !Number.isFinite(month) ||
      !Number.isFinite(day) ||
      month < 1 ||
      month > 12 ||
      day < 1 ||
      day > 31
    ) {
      return null;
    }

    return new Date(year, month - 1, day, 0, 0, 0, 0);
  }

  private parseDayKey(value: string | null): string | null {
    if (!value) {
      return null;
    }

    return this.parseDateKey(value) ? value : null;
  }

  private formatMonthParam(date: Date): string {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    return `${year}-${month}`;
  }

  private setMonth(year: number, monthIndex: number): void {
    const nextMonth = this.startOfMonth(new Date(year, monthIndex, 1));
    this.monthDate.set(nextMonth);
    this.selectedDayKey.set(this.getDefaultSelectedDayKey(nextMonth));
    this.updateUrl();
  }

  private getDefaultSelectedDayKey(month: Date): string {
    const today = new Date();
    const isSameMonth =
      today.getFullYear() === month.getFullYear() && today.getMonth() === month.getMonth();

    return this.toLocalDateKey(isSameMonth ? today : month);
  }

  private startOfMonth(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), 1, 0, 0, 0, 0);
  }

  private toLocalDateKey(date: Date): string {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private formatTime(iso: string): string {
    return new Date(iso).toLocaleTimeString('pl-PL', {
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  protected formatDayShort(cell: TimesheetCalendarDayCell): string {
    return cell.date.toLocaleDateString('pl-PL', {
      weekday: 'short',
      day: '2-digit',
      month: '2-digit',
    });
  }

  protected formatWeekRange(week: TimesheetCalendarDayCell[]): string {
    if (week.length === 0) {
      return '';
    }

    const start = week[0].date;
    const end = week[week.length - 1].date;
    return `${start.toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit' })} - ${end.toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit' })}`;
  }
}
